import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
    GET_PRICING_PLANS,
    GET_MY_SUBSCRIPTION,
    CREATE_CHECKOUT_SESSION,
    CREATE_BILLING_PORTAL_SESSION,
    CANCEL_SUBSCRIPTION,
    CREATE_SUBSCRIPTION,
    UPDATE_SUBSCRIPTION,
    GET_PAYMENT_METHODS,
    ADD_PAYMENT_METHOD,
    DELETE_PAYMENT_METHOD,
    SET_DEFAULT_PAYMENT_METHOD,
} from "../graphql/operations/subscription";

export type BillingCurrency = "usd" | "inr";

export function asBillingCurrency(code: string): BillingCurrency {
    return code === "inr" ? "inr" : "usd";
}

export interface Feature {
    name: string;
    code: string;
    included: boolean;
    limit: number | null;
    period: "monthly" | "yearly" | "daily" | "weekly" | null;
}

export interface Price {
    currency: string;
    monthlyPrice: number;
    yearlyPrice: number;
    stripePriceIdMonthly?: string | null;
    stripePriceIdYearly?: string | null;
    razorpayPlanIdMonthly?: string | null;
    razorpayPlanIdYearly?: string | null;
}

export interface SetupFeePrice {
    currency: string;
    amount: number;
    chatAmount?: number;
    callAmount?: number;
}

export type SetupFeeService = "chat" | "call";

export interface PricingPlan {
    id: string;
    name: string;
    displayName: string;
    description: string;
    setupFee?: number;
    setupFeeCurrency?: string | null;
    setupFeePrices?: SetupFeePrice[];
    prices: Price[];
    features: Feature[];
    isPopular: boolean;
    isActive: boolean;
    sortOrder: number;
}

export interface Subscription {
    id: string;
    organization: {
        id: string;
        name: string;
    };
    stripeCustomerId?: string | null;
    stripeSubscriptionId?: string | null;
    stripePriceId?: string | null;
    razorpayCustomerId?: string | null;
    razorpaySubscriptionId?: string | null;
    razorpayPlanId?: string | null;
    provider?: "stripe" | "shopify" | "manual" | "razorpay";
    shopifyChargeId?: string | null;
    shopifyStatus?: string | null;
    shopifyConfirmationUrl?: string | null;
    plan: string;
    status: string;
    currency: string;
    amount: number;
    billingCycle: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    trialStart?: string | null;
    trialEnd?: string | null;
    cancelAtPeriodEnd: boolean;
    canceledAt?: string | null;
    metadata: any;
    createdAt: string;
    updatedAt: string;
}

export interface SavedPaymentMethod {
    id: string;
    stripePaymentMethodId: string;
    type: string;
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
    isDefault: boolean;
    createdAt?: string;
}

export interface SavedPaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
  createdAt?: string;
}

export interface SubscriptionState {
    pricingPlans: PricingPlan[];
    currentSubscription: Subscription | null;
    paymentMethods: SavedPaymentMethod[];
    isLoading: boolean;
    error: string | null;
    selectedCurrency: "usd" | "inr";
    selectedBillingCycle: "monthly" | "yearly";
}

export interface SubscriptionActions {
    // Pricing plans
    fetchPricingPlans: (client: ApolloClient<NormalizedCacheObject>, currency?: "usd" | "inr") => Promise<void>;

    // Subscription management
    fetchMySubscription: (client: ApolloClient<NormalizedCacheObject>, organizationId?: string | null, forceNetwork?: boolean) => Promise<void>;
    createSubscriptionByPrice: (
        client: ApolloClient<NormalizedCacheObject>,
        userId: string,
        priceId: string,
        paymentMethodId?: string | null,
        savedPaymentMethodId?: string | null,
        applySetupFee?: boolean,
        organizationId?: string | null,
        setupFeeServices?: SetupFeeService[] | null,
    ) => Promise<boolean>;
    updateSubscriptionByPrice: (
        client: ApolloClient<NormalizedCacheObject>,
        userId: string,
        subscriptionId: string,
        newPriceId: string,
        paymentMethodId?: string | null,
        savedPaymentMethodId?: string | null,
        applySetupFee?: boolean,
        organizationId?: string | null,
        setupFeeServices?: SetupFeeService[] | null,
    ) => Promise<boolean>;
    createCheckoutSession: (client: ApolloClient<NormalizedCacheObject>, plan: string, billingCycle: "monthly" | "yearly", currency: "usd" | "inr", applySetupFee?: boolean, setupFeeServices?: SetupFeeService[] | null) => Promise<string | null>;
    createBillingPortalSession: (client: ApolloClient<NormalizedCacheObject>) => Promise<string | null>;
    cancelSubscription: (client: ApolloClient<NormalizedCacheObject>, userId: string, subscriptionId: string, cancelAtPeriodEnd?: boolean, organizationId?: string | null) => Promise<boolean>;

    // Payment method management
    fetchPaymentMethods: (client: ApolloClient<NormalizedCacheObject>) => Promise<void>;
    addPaymentMethod: (client: ApolloClient<NormalizedCacheObject>, paymentMethodId: string) => Promise<SavedPaymentMethod | null>;
    deletePaymentMethod: (client: ApolloClient<NormalizedCacheObject>, id: string) => Promise<boolean>;
    setDefaultPaymentMethod: (client: ApolloClient<NormalizedCacheObject>, id: string) => Promise<void>;

    // UI state
    setSelectedCurrency: (currency: BillingCurrency) => void;
    setSelectedBillingCycle: (cycle: "monthly" | "yearly") => void;
    clearError: () => void;
}

const useSubscriptionStore = create<SubscriptionState & SubscriptionActions>()(
    persist(
        (set, get) => ({
            // Initial state
            pricingPlans: [],
            currentSubscription: null,
            paymentMethods: [],
            isLoading: false,
            error: null,
            selectedCurrency: "usd",
            selectedBillingCycle: "monthly",
            // Pricing plans
            fetchPricingPlans: async (client: ApolloClient<NormalizedCacheObject>, _currency?: "usd" | "inr") => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.query({
                        query: GET_PRICING_PLANS,
                        fetchPolicy: "network-only",
                    });

                    if (data?.pricingPlans) {
                        const availableCurrencies = Array.from(new Set(data.pricingPlans.flatMap((plan: PricingPlan) => (Array.isArray(plan.prices) ? plan.prices.map((price) => String(price.currency || "").toLowerCase()) : [])).filter(Boolean)));
                        const currentSelected = get().selectedCurrency;
                        const nextSelected = availableCurrencies.includes(currentSelected)
                            ? currentSelected
                            : availableCurrencies[0] === "usd" || availableCurrencies[0] === "inr"
                              ? availableCurrencies[0]
                              : currentSelected === "usd" || currentSelected === "inr"
                                ? currentSelected
                                : "usd";

                        set({
                            pricingPlans: data.pricingPlans,
                            selectedCurrency: nextSelected,
                            isLoading: false,
                            error: null,
                        });
                    } else {
                        set({ isLoading: false, error: "Failed to fetch pricing plans" });
                    }
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to fetch pricing plans";
                    set({ isLoading: false, error: errorMessage });
                }
            },

            // Subscription management
            fetchMySubscription: async (client: ApolloClient<NormalizedCacheObject>, organizationId?: string | null, forceNetwork: boolean = false) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.query({
                        query: GET_MY_SUBSCRIPTION,
                        variables: { organizationId: organizationId || null },
                        fetchPolicy: forceNetwork ? "network-only" : "cache-first",
                    });

                    if (data?.mySubscription) {
                        set({
                            currentSubscription: data.mySubscription,
                            isLoading: false,
                            error: null,
                        });
                    } else {
                        set({
                            currentSubscription: null,
                            isLoading: false,
                            error: null,
                        });
                    }
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to fetch subscription";
                    set({ isLoading: false, error: errorMessage });
                }
            },

            createSubscriptionByPrice: async (
                client: ApolloClient<NormalizedCacheObject>,
                userId: string,
                priceId: string,
                paymentMethodId?: string | null,
                savedPaymentMethodId?: string | null,
                applySetupFee: boolean = false,
                organizationId?: string | null,
                setupFeeServices?: SetupFeeService[] | null,
            ) => {
                try {
                    set({ isLoading: true, error: null });
                    const { data } = await client.mutate({
                        mutation: CREATE_SUBSCRIPTION,
                        variables: {
                            userId,
                            priceId,
                            paymentMethodId: paymentMethodId || null,
                            savedPaymentMethodId: savedPaymentMethodId || null,
                            applySetupFee,
                            organizationId: organizationId || null,
                            setupFeeServices: setupFeeServices && setupFeeServices.length > 0 ? setupFeeServices : null,
                        },
                    });
                    if (data?.createSubscription) {
                        set({ currentSubscription: data.createSubscription, isLoading: false, error: null });
                        return true;
                    }
                    set({ isLoading: false, error: "Failed to create subscription" });
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to create subscription";
                    set({ isLoading: false, error: errorMessage });
                    return false;
                }
            },

            updateSubscriptionByPrice: async (
                client: ApolloClient<NormalizedCacheObject>,
                userId: string,
                subscriptionId: string,
                newPriceId: string,
                paymentMethodId?: string | null,
                savedPaymentMethodId?: string | null,
                applySetupFee: boolean = false,
                organizationId?: string | null,
                setupFeeServices?: SetupFeeService[] | null,
            ) => {
                try {
                    set({ isLoading: true, error: null });
                    const { data } = await client.mutate({
                        mutation: UPDATE_SUBSCRIPTION,
                        variables: {
                            userId,
                            subscriptionId,
                            newPriceId,
                            paymentMethodId: paymentMethodId || null,
                            savedPaymentMethodId: savedPaymentMethodId || null,
                            applySetupFee,
                            organizationId: organizationId || null,
                            setupFeeServices: setupFeeServices && setupFeeServices.length > 0 ? setupFeeServices : null,
                        },
                    });
                    if (data?.updateSubscription) {
                        set({ currentSubscription: data.updateSubscription, isLoading: false, error: null });
                        return true;
                    }
                    set({ isLoading: false, error: "Failed to update subscription" });
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to update subscription";
                    set({ isLoading: false, error: errorMessage });
                    return false;
                }
            },

            createCheckoutSession: async (client: ApolloClient<NormalizedCacheObject>, plan: string, billingCycle: "monthly" | "yearly", currency: "usd" | "inr", applySetupFee: boolean = false, setupFeeServices?: SetupFeeService[] | null) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.mutate({
                        mutation: CREATE_CHECKOUT_SESSION,
                        variables: {
                            input: {
                                plan,
                                billingCycle,
                                currency,
                                applySetupFee,
                                setupFeeServices: setupFeeServices && setupFeeServices.length > 0 ? setupFeeServices : null,
                            },
                        },
                    });
                    if (data?.createCheckoutSession?.url) {
                        set({ isLoading: false, error: null });
                        return data.createCheckoutSession.url;
                    }

                    set({ isLoading: false, error: "Failed to create checkout session" });
                    return null;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to create checkout session";
                    set({ isLoading: false, error: errorMessage });
                    return null;
                }
            },

            createBillingPortalSession: async (client: ApolloClient<NormalizedCacheObject>) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.mutate({
                        mutation: CREATE_BILLING_PORTAL_SESSION,
                    });

                    if (data?.createBillingPortalSession?.url) {
                        set({ isLoading: false, error: null });
                        return data.createBillingPortalSession.url;
                    }

                    set({ isLoading: false, error: "Failed to create billing portal session" });
                    return null;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to create billing portal session";
                    set({ isLoading: false, error: errorMessage });
                    return null;
                }
            },

            cancelSubscription: async (client: ApolloClient<NormalizedCacheObject>, userId: string, subscriptionId: string, cancelAtPeriodEnd: boolean = true, organizationId?: string | null) => {
                try {
                    set({ isLoading: true, error: null });

                    const { data } = await client.mutate({
                        mutation: CANCEL_SUBSCRIPTION,
                        variables: { userId, subscriptionId, cancelAtPeriodEnd, organizationId: organizationId || null },
                    });

                    if (data?.cancelSubscription) {
                        set({
                            currentSubscription: data.cancelSubscription,
                            isLoading: false,
                            error: null,
                        });
                        return true;
                    }

                    set({ isLoading: false, error: "Failed to cancel subscription" });
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to cancel subscription";
                    set({ isLoading: false, error: errorMessage });
                    return false;
                }
            },

            // Payment method management
            fetchPaymentMethods: async (client: ApolloClient<NormalizedCacheObject>) => {
                try {
                    const { data } = await client.query({
                        query: GET_PAYMENT_METHODS,
                        fetchPolicy: "network-only",
                    });
                    if (data?.myPaymentMethods) {
                        set({ paymentMethods: data.myPaymentMethods });
                    }
                } catch (error: any) {
                    console.error("Failed to fetch payment methods:", error);
                }
            },

            // UI state
            setSelectedCurrency: (currency: BillingCurrency) => {
                set({ selectedCurrency: currency });
            },

            clearError: () => {
                set({ error: null });
            },

            addPaymentMethod: async (client: ApolloClient<NormalizedCacheObject>, paymentMethodId: string) => {
                try {
                    const { data } = await client.mutate({
                        mutation: ADD_PAYMENT_METHOD,
                        variables: { paymentMethodId },
                    });
                    if (data?.addPaymentMethod) {
                        set((state) => ({
                            paymentMethods: [data.addPaymentMethod, ...state.paymentMethods.filter((pm) => pm.stripePaymentMethodId !== data.addPaymentMethod.stripePaymentMethodId)],
                        }));
                        return data.addPaymentMethod;
                    }
                    return null;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to add payment method";
                    set({ error: errorMessage });
                    return null;
                }
            },

            deletePaymentMethod: async (client: ApolloClient<NormalizedCacheObject>, id: string) => {
                try {
                    const { data } = await client.mutate({
                        mutation: DELETE_PAYMENT_METHOD,
                        variables: { id },
                    });
                    if (data?.deletePaymentMethod) {
                        set((state) => ({
                            paymentMethods: state.paymentMethods.filter((pm) => pm.id !== id),
                        }));
                        return true;
                    }
                    return false;
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to delete payment method";
                    set({ error: errorMessage });
                    return false;
                }
            },

            setDefaultPaymentMethod: async (client: ApolloClient<NormalizedCacheObject>, id: string) => {
                try {
                    const { data } = await client.mutate({
                        mutation: SET_DEFAULT_PAYMENT_METHOD,
                        variables: { id },
                    });
                    if (data?.setDefaultPaymentMethod) {
                        // Update isDefault flags in local state based on server response
                        const updatedIds = new Set(data.setDefaultPaymentMethod.filter((pm: any) => pm.isDefault).map((pm: any) => pm.id));
                        set((state) => ({
                            paymentMethods: state.paymentMethods.map((pm) => ({
                                ...pm,
                                isDefault: updatedIds.has(pm.id),
                            })),
                        }));
                    }
                } catch (error: any) {
                    const errorMessage = error.graphQLErrors?.[0]?.message || error.message || "Failed to set default payment method";
                    set({ error: errorMessage });
                }
            },

            setSelectedBillingCycle: (cycle: "monthly" | "yearly") => {
                set({ selectedBillingCycle: cycle });
            },
        }),
        {
            name: "subscription-storage",
            partialize: (state) => ({
                selectedCurrency: state.selectedCurrency,
                selectedBillingCycle: state.selectedBillingCycle,
            }),
        },
    ),
);

export default useSubscriptionStore;
export { useSubscriptionStore };
